/**
 * Browser wrapper around {@link drawSheet}.
 *
 * Creates a canvas at the picture's native resolution for each sheet, draws, and encodes to
 * a PNG data URL. Synchronous, per ADR-0009 — the handoff's `result: {quiz, solution}` state
 * shape verbatim. That ADR records the cost (a main-thread block on very large pictures) and
 * the measurement that would justify moving to `toBlob` or a worker.
 */

import { drawSheet, type SheetKind, type DrawOptions } from './draw';
import type { ImageRect } from './geometry';

/** The two generated sheets, as PNG data URLs. */
export interface Sheets {
  /** Player sheet — cells blacked out. */
  readonly quiz: string;
  /** Answer key — gridlines only. */
  readonly solution: string;
}

/** Download filenames, fixed by the handoff. */
export const FILENAMES: Record<keyof Sheets, string> = {
  quiz: 'bingo-players.png',
  solution: 'bingo-answer-key.png',
};

/**
 * Thrown when the canvas has been tainted by a cross-origin picture and cannot be exported.
 * The message is the handoff's copy, shown to the user as-is.
 */
export class TaintedCanvasError extends Error {
  constructor() {
    super(
      "That picture's host blocks copying, so it can't be exported. " +
        'Upload it instead, or try another link.',
    );
    this.name = 'TaintedCanvasError';
  }
}

export interface RenderOptions {
  readonly image: CanvasImageSource;
  readonly natural: { readonly w: number; readonly h: number };
  readonly rect: ImageRect;
  readonly lineColor?: string;
  readonly blackoutColor?: string;
  readonly labelScale?: number;
  readonly n?: number;
}

function renderOne(kind: SheetKind, opts: RenderOptions): string {
  const canvas = document.createElement('canvas');
  canvas.width = opts.natural.w;
  canvas.height = opts.natural.h;

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Could not get a 2D drawing context');

  // Only pass through the optional fields that were actually supplied —
  // exactOptionalPropertyTypes means an explicit `undefined` is not the same as absent.
  const drawOptions: DrawOptions = {
    image: opts.image,
    natural: opts.natural,
    rect: opts.rect,
    kind,
    ...(opts.lineColor !== undefined && { lineColor: opts.lineColor }),
    ...(opts.blackoutColor !== undefined && { blackoutColor: opts.blackoutColor }),
    ...(opts.labelScale !== undefined && { labelScale: opts.labelScale }),
    ...(opts.n !== undefined && { n: opts.n }),
  };

  drawSheet(ctx, drawOptions);

  try {
    return canvas.toDataURL('image/png');
  } catch {
    // A cross-origin picture without permissive CORS headers taints the canvas and
    // toDataURL throws SecurityError. In production the proxy (ADR-0002) prevents this;
    // this path covers a proxy failure or a direct source.
    throw new TaintedCanvasError();
  }
}

/**
 * Render both sheets.
 *
 * @throws {TaintedCanvasError} if the picture tainted the canvas.
 */
export function renderSheets(opts: RenderOptions): Sheets {
  return {
    quiz: renderOne('quiz', opts),
    solution: renderOne('solution', opts),
  };
}

/** Trigger a browser download of one sheet. */
export function downloadSheet(sheets: Sheets, which: keyof Sheets): void {
  const anchor = document.createElement('a');
  anchor.href = sheets[which];
  anchor.download = FILENAMES[which];
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Gap between the two downloads, so browsers do not swallow the second. */
export const DOWNLOAD_GAP_MS = 350;

/**
 * Download both sheets. The second fires after {@link DOWNLOAD_GAP_MS} because browsers
 * discard a second programmatic download that arrives too soon after the first.
 *
 * @returns a cancel function, so a unmounting view can drop the pending second download.
 */
export function downloadBoth(sheets: Sheets): () => void {
  downloadSheet(sheets, 'quiz');
  const timer = setTimeout(() => downloadSheet(sheets, 'solution'), DOWNLOAD_GAP_MS);
  return () => clearTimeout(timer);
}
